/**
 * Scripted Yogi conversation flow ported from legacy Chatbot.js
 * @param {{ firstName?: string, name?: string, talkedWithYogi?: boolean }} user
 */
export function buildYogiSteps(user) {
  const firstName = user.firstName || user.name?.split(' ')[0] || 'friend';
  const welcomePrefix = user.talkedWithYogi ? `Welcome back ${firstName}, ` : `${firstName}, `;

  /** @type {Record<string, import('./yogiTypes.js').YogiStep>} */
  const steps = {
    '0': {
      type: 'message',
      message: `${welcomePrefix}Everyone can do yoga. Do you know yoga can help you manage the side effects of cancer and its treatments?`,
      trigger: 'yesNo1',
    },
    yesNo1: {
      type: 'options',
      options: [
        { value: 'yes', label: 'Yes', trigger: 'YesAnswer1' },
        { value: 'no', label: 'No', trigger: 'noAnswer1' },
      ],
    },
    noAnswer1: {
      type: 'message',
      message:
        'We have fantastic news! Scientific research has shown that yoga can help relieve side effects from cancer and its treatment. Would you like to know more about it?',
      trigger: 'yesNotNow1',
    },
    yesNotNow1: {
      type: 'options',
      options: [
        { value: 'yes', label: 'Yes', trigger: 'knowMoreAboutIt' },
        { value: 'not now', label: 'Not now', trigger: 'Understandable' },
      ],
    },
    Understandable: {
      type: 'message',
      message:
        'Understandable! You can come back any time to learn how yoga can help you during your survivorship!',
      trigger: 'weKnowYouAreBusy',
    },
    knowMoreAboutIt: {
      type: 'message',
      message:
        'Click to learn the "Effects of Yoga on Cancer-related Side Effects"\nClick to listen to "Scientists Share Their Research Findings on Yoga"\nClick to listen to "Oncologists and Care Providers\' Recommendations"',
      trigger: 'trySomeYogaNow',
    },
    trySomeYogaNow: {
      type: 'message',
      message: 'Would you like to try some yoga now?',
      trigger: 'yesNo2',
    },
    yesNo2: {
      type: 'options',
      options: [
        { value: 'yes', label: 'Yes', trigger: 'YesAnswer1' },
        { value: 'no', label: 'No', trigger: 'weKnowYouAreBusy' },
      ],
    },
    YesAnswer1: {
      type: 'message',
      message: 'What would you like yoga to help you with today?',
      trigger: 'symptomSelection',
    },
    symptomSelection: {
      type: 'options',
      options: [
        { value: 'Difficulty Sleeping', label: 'Difficulty Sleeping', trigger: 'tellUsAboutYourSeverity' },
        { value: 'Fatigue', label: 'Fatigue', trigger: 'tellUsAboutYourSeverity' },
        { value: 'Memory Difficulty', label: 'Memory Difficulty', trigger: 'tellUsAboutYourSeverity' },
        { value: 'Pain', label: 'Pain', trigger: 'tellUsAboutYourSeverity' },
        { value: 'Low Mood', label: 'Low Mood', trigger: 'tellUsAboutYourSeverity' },
        { value: 'Depression', label: 'Depression', trigger: 'tellUsAboutYourSeverity' },
        { value: 'Anxiety', label: 'Anxiety', trigger: 'tellUsAboutYourSeverity' },
        { value: 'Physical Function', label: 'Physical Function', trigger: 'tellUsAboutYourSeverity' },
        { value: 'Other', label: 'Other', trigger: 'tellUsAboutYourSeverity' },
      ],
      storeAs: 'symptom',
    },
    tellUsAboutYourSeverity: {
      type: 'message',
      message: 'Please tell us about the severity of your {previousValue}: [1=mild, 10= as severe as you can imagine]',
      trigger: 'symptomSeverity',
    },
    symptomSeverity: {
      type: 'options',
      options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map((n) => ({
        value: n,
        label: n,
        trigger: 'thankYouImSorry',
      })),
    },
    thankYouImSorry: {
      type: 'component',
      component: 'thankYouSorry',
      trigger: 'yesNo3',
    },
    yesNo3: {
      type: 'options',
      options: [
        { value: 'yes', label: 'Yes', trigger: 'awesomeChooseYogaClass' },
        { value: 'no', label: 'Not now', trigger: 'weKnowYouAreBusy' },
      ],
    },
    awesomeChooseYogaClass: {
      type: 'message',
      message: 'Awesome! Click on the yoga class you prefer! Enjoy!',
      trigger: 'linksToYogaClasses',
    },
    linksToYogaClasses: {
      type: 'component',
      component: 'videoLinks',
      trigger: 'didYouEnjoy',
    },
    didYouEnjoy: {
      type: 'message',
      message: 'Awesome! Click on the yoga class you prefer! Enjoy!',
      end: true,
    },
    weKnowYouAreBusy: {
      type: 'message',
      message: 'We know you are busy. Would you like to schedule your yoga at different time?',
      trigger: 'yesNotSure',
    },
    yesNotSure: {
      type: 'options',
      options: [
        { value: 'yes', label: 'Yes', trigger: 'whatDayAndTime' },
        { value: 'notSure', label: 'I am not sure', trigger: '4' },
      ],
    },
    whatDayAndTime: {
      type: 'message',
      message: 'What day and time would you like to do your yoga?',
      trigger: 'date',
    },
    date: {
      type: 'component',
      component: 'reminderPicker',
      trigger: '2',
    },
    '2': {
      type: 'message',
      message: 'Congratulations, yogi! Your upcoming yoga session at {previousValue}',
      end: true,
    },
    '4': {
      type: 'component',
      component: 'itsOk',
      end: true,
    },
  };

  return steps;
}

/**
 * @param {string} text
 * @param {{ previousValue?: string, symptom?: string }} context
 */
export function resolveYogiText(text, context) {
  return text.replace(/\{previousValue\}/g, context.previousValue || context.symptom || '');
}
